import { useState } from 'react';
import { formatMoney } from './charts/IbcsTooltip';
import type { ForecastWeek, ForecastFlowType } from '../../types/cashCockpit';
import ForecastBreakdownModal from './ForecastBreakdownModal';

interface Props {
    forecast: ForecastWeek[];
    displayCurrency?: string;
    title?: string;
    /** Entities used for the drill-down query. Required if `onDrillDown` is not provided. */
    entities?: string[];
    /** Currency mode passed to the backend ('ALL' or ISO code). Defaults to displayCurrency. */
    currencyMode?: string;
    /**
     * Custom drill-down handler; overrides the built-in modal. Useful if the
     * host page wants its own modal implementation.
     */
    onDrillDown?: (week: ForecastWeek, flowType: ForecastFlowType) => void;
}

/**
 * Weekly forecast detail table — Week/Starting/Opening/AR In/AP Out/
 * Planned In/Planned Out/Net/Closing. Numeric cells for AR/AP/Planned
 * are clickable and open a drill-down modal listing the underlying rows.
 */
export default function WeekDetailTable({
    forecast, displayCurrency = 'EUR', title = 'Week Detail',
    entities, currencyMode, onDrillDown,
}: Props) {
    const [drill, setDrill] = useState<{ week: ForecastWeek; flowType: ForecastFlowType } | null>(null);
    if (!forecast.length) return null;
    const fmt = (n: number) => formatMoney(n, displayCurrency);
    const canDrill = !!entities && entities.length > 0;

    const handleClick = (week: ForecastWeek, flowType: ForecastFlowType, amount: number) => {
        if (amount === 0) return;
        if (onDrillDown) onDrillDown(week, flowType);
        else if (canDrill) setDrill({ week, flowType });
    };

    const cellClass = (amount: number) =>
        amount !== 0 && canDrill
            ? 'cursor-pointer hover:bg-blue-50 hover:underline decoration-dotted underline-offset-2'
            : '';

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                {title}
                <span className="text-xs font-normal text-gray-500">
                    (values in {displayCurrency}
                    {canDrill ? ' — click a value to see details' : ''})
                </span>
            </h3>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-2 py-2 text-left">Week</th>
                            <th className="px-2 py-2 text-left">Starting</th>
                            <th className="px-2 py-2 text-right">Opening</th>
                            <th className="px-2 py-2 text-right">AR In</th>
                            <th className="px-2 py-2 text-right">AP Out</th>
                            <th className="px-2 py-2 text-right">Planned In</th>
                            <th className="px-2 py-2 text-right">Planned Out</th>
                            <th className="px-2 py-2 text-right">Net</th>
                            <th className="px-2 py-2 text-right">Closing</th>
                        </tr>
                    </thead>
                    <tbody>
                        {forecast.map(w => (
                            <tr key={w.weekNumber} className="border-t border-gray-100">
                                <td className="px-2 py-1.5 font-semibold">W{w.weekNumber}</td>
                                <td className="px-2 py-1.5 text-gray-600">{w.weekStart}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{fmt(w.openingCashEUR)}</td>
                                <td
                                    className={`px-2 py-1.5 text-right font-mono text-[#2C7BB6] ${cellClass(w.arInflowEUR)}`}
                                    onClick={() => handleClick(w, 'ar', w.arInflowEUR)}
                                    title={w.arInflowEUR !== 0 && canDrill ? 'Click to see open AR details' : undefined}
                                >{fmt(w.arInflowEUR)}</td>
                                <td
                                    className={`px-2 py-1.5 text-right font-mono text-[#D7191C] ${cellClass(w.apOutflowEUR)}`}
                                    onClick={() => handleClick(w, 'ap', w.apOutflowEUR)}
                                    title={w.apOutflowEUR !== 0 && canDrill ? 'Click to see open AP details' : undefined}
                                >{fmt(w.apOutflowEUR)}</td>
                                <td
                                    className={`px-2 py-1.5 text-right font-mono text-[#2C7BB6] ${cellClass(w.plannedInflowEUR)}`}
                                    onClick={() => handleClick(w, 'plannedIn', w.plannedInflowEUR)}
                                    title={w.plannedInflowEUR !== 0 && canDrill ? 'Click to see planned inflow events' : undefined}
                                >{fmt(w.plannedInflowEUR)}</td>
                                <td
                                    className={`px-2 py-1.5 text-right font-mono text-[#D7191C] ${cellClass(w.plannedOutflowEUR)}`}
                                    onClick={() => handleClick(w, 'plannedOut', w.plannedOutflowEUR)}
                                    title={w.plannedOutflowEUR !== 0 && canDrill ? 'Click to see planned outflow events' : undefined}
                                >{fmt(w.plannedOutflowEUR)}</td>
                                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${w.netChangeEUR >= 0 ? 'text-[#2C7BB6]' : 'text-[#D7191C]'}`}>
                                    {fmt(w.netChangeEUR)}
                                </td>
                                <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmt(w.closingCashEUR)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {drill && entities && (
                <ForecastBreakdownModal
                    entities={entities}
                    weekStart={drill.week.weekStart}
                    weekEnd={drill.week.weekEnd}
                    weekNumber={drill.week.weekNumber}
                    flowType={drill.flowType}
                    currency={currencyMode ?? displayCurrency}
                    onClose={() => setDrill(null)}
                />
            )}
        </div>
    );
}
